/// <reference path="../../setup/_references.ts" />
module onvio.practice.widgets {
    'use strict';

    interface IPeriodObject extends bmp.IDateRangeSelectorInput {
        startDate?: Date;
        endDate?: Date;
        /** Details */
        details?: any;
        comparativePeriodId: string;
    }

    export class BmPracticeWidgetKeyTotals implements IBmPracticeWidgetService {

        /** Flag if the widget is busy */
        public isBusy: boolean = false;

        /** Ensure the invoice widgets refresh together */
        public coalescingServiceArea: string = 'invoices';

        /** Templates for the widget */
        public bodyTemplate: string = 'practice-widgets/key-totals/key-totals.html';
        public headerTemplate: string = 'practice-widgets/practice-widget-date-search-header.html';
        public searchBox: boolean = false;
        public dateSelector: boolean = false;
        public size: WidgetSize = 'large';

        /** Key total indicators */
        public indicators: cs.INamedReferenceDto[];

        /** Key total periods */
        public periods: IPeriodObject[];
        private PTD = 'currentPeriod';
        private YTD = 'yearToCurrentPeriod';

        /** Deferred initialization of the widget header */
        private _initializeWidgetHeader: any;

        static $inject = [
            '$q',
            '$translate',
            'csKeyTotalsV1',
            'bmpBigNumberUtils',
        ];
        constructor(
            private $q: ng.IQService,
            private $translate: ng.translate.ITranslateService,
            private dataService: cs.IKeyTotalsV1Service,
            private bigNumber: bmp.globalize.IBigNumberUtils) {

            this.indicators = [
                { id: 'produced', name: this.$translate.instant('Core.Captions.KeyTotalAmounts.Produced') },
                { id: 'adjusted', name: this.$translate.instant('Core.Captions.KeyTotalAmounts.Adjusted') },
                { id: 'invoiced', name: this.$translate.instant('Core.Captions.KeyTotalAmounts.Invoiced') },
                { id: 'realizationPercentage', name: this.$translate.instant('Core.Captions.KeyTotalAmounts.RealizationPercentage') },
                { id: 'collected', name: this.$translate.instant('Core.Captions.KeyTotalAmounts.Collected') }
            ];

            this.periods = [
                {
                    id: this.PTD,
                    name: this.$translate.instant('Core.Captions.KeyTotals.CurrentPeriod'),
                    showStartDate: true,
                    showEndDate: true,
                    comparativePeriodId: 'priorYearCurrentPeriod'
                },
                {
                    id: this.YTD,
                    name: this.$translate.instant('Core.Captions.KeyTotals.YearToCurrentPeriod'),
                    showStartDate: true,
                    showEndDate: true,
                    comparativePeriodId: 'priorYearToCurrentPeriod'
                }
            ];

        }

        public initialize(widget: IBmPracticeWidgetController, done: () => void): void {
            // Allow deferred initialization of the header until after the dates are loaded
            this._initializeWidgetHeader = () => {
                this.dateSelector = true;
                widget.dateRangeSelectorOptions = this.periods;
                widget.dateRangeSelectorDefault = this.periods[0].id
                done();
            };
        }

        public loadData(args: IBmPracticeWidgetLoadDataArgs): ng.IPromise<any> {

            return this.dataService.get({ clientId: args.contextId }).then((r: cs.IKeyTotalsV1Dto & { [index: string]: any }) => {

                // Update the periods to reflect the current dates returned in the data
                _.forEach(this.periods, (period: IPeriodObject) => {
                    switch (period.id) {
                        case this.PTD:
                            period.calc = () => { return { startDate: moment(r.currentPeriodStartDate).toDate(), endDate: moment(r.currentPeriodEndDate).toDate() } };
                            break;
                        case this.YTD:
                            let fiscalYearEndDate = moment(r.fiscalYearStartDate).add(1, 'years').subtract(1, 'days');
                            period.calc = () => { return { startDate: moment(r.fiscalYearStartDate).toDate(), endDate: fiscalYearEndDate.toDate() } };
                            break;
                    }
                });

                let data: any = {};
                _.forEach(this.periods, (period: IPeriodObject) => {
                    let periodData = data[period.id] = r[period.id];
                    let comparativeData = r[period.comparativePeriodId];
                    _.forEach(this.indicators, (indicator: cs.INamedReferenceDto) => {
                        let parsedValue = this.bigNumber.safeParseBigNumber(periodData[indicator.id]);
                        periodData[indicator.id + 'Formatted'] = this.bigNumber.formatDecimal(parsedValue, indicator.id === 'realizationPercentage' ? 'p' : 'c');
                        let parsedComparativeValue = this.bigNumber.safeParseBigNumber(comparativeData[indicator.id]);
                        this._generateChartData(periodData, indicator.id, parsedValue.toNumber(), parsedComparativeValue.toNumber());
                    });
                });


                this._initializeWidgetHeader();

                data.period = data[args.dateRangeId];
                return data;

            }).catch(() => {
                return undefined;
            });

        }

        /**
         * Generates the chart data
         * @param data  Data object
         * @param summary Summary identifier
         */
        private _generateChartData(data: any, summaryId: string, value: number, comparativeValue: number): void {

            // Generate the default 'blank' chart data
            let chartData = data[summaryId + 'Chart'] = {
                negativeLineStart: '50%',
                positiveLineEnd: '50%',
                tooltip: this.$translate.instant('Bluemoon.Practice.Widget.KeyTotals.ComparativeValueTooltip', {
                    value: this.bigNumber.formatDecimal(value, summaryId === 'realizationPercentage' ? 'p' : 'c'),
                    comparativeValue: this.bigNumber.formatDecimal(comparativeValue, summaryId === 'realizationPercentage' ? 'p' : 'c')
                }),
                changeText: undefined as string
            };

            // Determine the change and text
            let percentChange = 0;
            if ((value === 0 && comparativeValue === 0) || value === comparativeValue) {
                chartData.changeText = this.$translate.instant('Bluemoon.Practice.Widget.KeyTotals.NoChange');
            } else if (value <= 0 && comparativeValue >= 0) {
                chartData.changeText = this.$translate.instant('Bluemoon.Practice.Widget.KeyTotals.NegativeChange');
                percentChange = -100;
            } else if (value >= 0 && comparativeValue <= 0) {
                chartData.changeText = this.$translate.instant('Bluemoon.Practice.Widget.KeyTotals.PositiveChange');
                percentChange = 100;
            } else {
                percentChange = Math.round(((value - comparativeValue) / Math.abs(comparativeValue)) * 100);
                chartData.changeText = this.bigNumber.formatDecimal(percentChange, 'p0');
            }

            // Determine the part of the line chart to draw
            percentChange = Math.min(100, Math.max(-100, percentChange));
            if (percentChange > 0) {
                chartData.positiveLineEnd = `${50 + (percentChange / 2)}%`;
            } else if (percentChange < 0) {
                chartData.negativeLineStart = `${(100 - Math.abs(percentChange)) / 2}%`;
            }

        }

    }

    angular.module('bm.practice.widgets.keyTotals',
        ['bento.d3', 'csKeyTotalsV1'])
        .service('bm-practice-widget-key-totals', BmPracticeWidgetKeyTotals);
}
